import {
  ArrayPath,
  Control,
  FieldErrors,
  FieldValues,
  Path,
  UseFormRegister,
  useFieldArray,
  useWatch,
} from "react-hook-form";
import { Plus, RotateCcw, Trash2 } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Input } from "components/ui/input";
import { Button } from "components/ui/button";

import { CampaignType, IPrizeRow } from "@/@types/gamification";
import {
  getDefaultPrizes,
  getFixedPrizeRowCount,
} from "../constants/mechanic-defaults";
import { ProbabilityPie } from "./probability-pie";

interface PrizeTableProps<T extends FieldValues> {
  campaignType: CampaignType;
  control: Control<T>;
  register: UseFormRegister<T>;
  errors: FieldErrors<T>;
}

type PrizeErrors = {
  mechanicConfig?: {
    prizes?: Array<{
      value?: { message?: string };
      probability?: { message?: string };
    }> & { message?: string; root?: { message?: string } };
  };
};

export function PrizeTable<T extends FieldValues>({
  campaignType,
  control,
  register,
  errors,
}: PrizeTableProps<T>) {
  const path = (p: string) => p as Path<T>;
  const prizesErrors = (errors as PrizeErrors).mechanicConfig?.prizes;

  const prizes = useFieldArray({
    control,
    name: "mechanicConfig.prizes" as ArrayPath<T>,
  });

  const watchPrizes = (useWatch({
    control,
    name: "mechanicConfig.prizes" as Path<T>,
  }) ?? []) as Array<Partial<IPrizeRow>>;

  const totalProbability = watchPrizes.reduce(
    (sum, prize) => sum + (Number(prize?.probability) || 0),
    0,
  );
  const isTotalValid = Math.abs(totalProbability - 100) < 0.01;
  const totalColor = isTotalValid ? "text-green-600" : "text-red-600";

  const fixedCount = getFixedPrizeRowCount(campaignType);
  const isLocked = fixedCount !== null;
  const hasWrongCount = isLocked && prizes.fields.length !== fixedCount;

  const handleReset = () =>
    prizes.replace(getDefaultPrizes(campaignType) as never[]);

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-y-2">
        <CardTitle>Prêmios da Campanha</CardTitle>
        <div className="flex flex-wrap items-center gap-4">
          <span className={`text-sm font-medium ${totalColor}`}>
            Total: {totalProbability.toFixed(2)}%
          </span>
          <Button type="button" variant="outline" size="sm" onClick={handleReset}>
            <RotateCcw className="mr-2 h-4 w-4" />
            Resetar padrão
          </Button>
          {!isLocked && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() =>
                prizes.append({ value: 0, probability: 0 } as never)
              }
            >
              <Plus className="mr-2 h-4 w-4" />
              Adicionar prêmio
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_220px]">
          <div className="space-y-3">
            <div
              className={`grid items-center gap-4 px-1 text-xs font-medium uppercase tracking-wide text-gray-500 ${
                isLocked
                  ? "grid-cols-[2rem_1fr_1fr]"
                  : "grid-cols-[2rem_1fr_1fr_auto]"
              }`}
            >
              <span>#</span>
              <span>Valor (R$)</span>
              <span>Porcentagem (%)</span>
              {!isLocked && <span className="w-9" aria-hidden="true" />}
            </div>

            {prizes.fields.map((field, index) => (
              <div
                key={field.id}
                className={`grid items-center gap-4 rounded-lg border p-3 ${
                  isLocked
                    ? "grid-cols-[2rem_1fr_1fr]"
                    : "grid-cols-[2rem_1fr_1fr_auto]"
                }`}
              >
                <span className="text-sm font-medium text-gray-500">
                  {index + 1}
                </span>
                <div>
                  <Input
                    id={`prize-${index}-value`}
                    type="number"
                    min="0"
                    step="0.01"
                    aria-label={`Valor do prêmio ${index + 1} (R$)`}
                    onWheel={(e) => e.currentTarget.blur()}
                    {...register(path(`mechanicConfig.prizes.${index}.value`))}
                  />
                  {prizesErrors?.[index]?.value?.message && (
                    <p className="mt-1 text-sm text-red-500">
                      {prizesErrors[index]?.value?.message}
                    </p>
                  )}
                </div>
                <div>
                  <Input
                    id={`prize-${index}-probability`}
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    aria-label={`Porcentagem do prêmio ${index + 1}`}
                    onWheel={(e) => e.currentTarget.blur()}
                    {...register(
                      path(`mechanicConfig.prizes.${index}.probability`),
                    )}
                  />
                  {prizesErrors?.[index]?.probability?.message && (
                    <p className="mt-1 text-sm text-red-500">
                      {prizesErrors[index]?.probability?.message}
                    </p>
                  )}
                </div>
                {!isLocked && (
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => prizes.remove(index)}
                    disabled={prizes.fields.length <= 1}
                    aria-label={`Remover prêmio ${index + 1}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}

            {hasWrongCount && (
              <p className="mt-2 flex items-center gap-2 text-sm text-amber-600">
                <span className="inline-block h-2 w-2 rounded-full bg-amber-500" />
                Esta mecânica exige exatamente {fixedCount} prêmios. Atual:{" "}
                {prizes.fields.length}/{fixedCount} — use "Resetar padrão".
              </p>
            )}

            {!isTotalValid && (
              <p className="text-sm text-red-500">
                A soma das porcentagens deve ser exatamente 100%. Atual:{" "}
                {totalProbability.toFixed(2)}%
              </p>
            )}

            {prizesErrors?.message && (
              <p className="text-sm text-red-500">{prizesErrors.message}</p>
            )}
          </div>

          <div className="rounded-lg border bg-gray-50/60 p-4">
            <p className="mb-3 text-sm font-medium text-gray-700">
              Distribuição
            </p>
            <ProbabilityPie prizes={watchPrizes} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
